import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const ContactUs = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [replyData, setReplyData] = useState({ id: '', subject: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('All');
  const [replyStatus, setReplyStatus] = useState('');
  const [sortBy, setSortBy] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);

  useEffect(() => {
    fetchContacts();
  }, [keyword, status, replyStatus, sortBy]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('contactUsList');
      let data = response.data || [];
      
      if (keyword) {
        data = data.filter(c => 
          c.fullName?.toLowerCase().includes(keyword.toLowerCase()) ||
          c.email?.toLowerCase().includes(keyword.toLowerCase()) ||
          c.subject?.toLowerCase().includes(keyword.toLowerCase())
        );
      }
      
      if (status !== 'All' && status !== '') {
        data = data.filter(c => c.status === parseInt(status));
      }
      
      if (replyStatus !== '' && replyStatus !== 'All') {
        data = data.filter(c => c.replyStatus === parseInt(replyStatus));
      }
      
      if (sortBy === '1') {
        data = data.filter(c => c.status === 1);
      } else if (sortBy === '0') {
        data = data.filter(c => c.status === 0);
      }
      
      setContacts(data);
      setCurrentPage(1);
    } catch (error) {
      toast.error('Failed to load contact us data');
      console.error('Contact fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      setSubmitting(true);
      await apiService.get(`deleteContactUs/${deleteId}`);
      setContacts(contacts.filter(contact => contact._id !== deleteId));
      setShowDeleteModal(false);
      toast.success('Contact info deleted successfully');
    } catch (error) {
      toast.error('Failed to delete contact info');
      console.error('Delete error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = (id) => {
    setReplyData({ id, subject: '', description: '' });
    setShowReplyModal(true);
  };

  const submitReply = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await apiService.post('contactReply', replyData);
      setContacts(contacts.map(c => c._id === replyData.id ? { ...c, replyStatus: 1 } : c));
      setShowReplyModal(false);
      toast.success('Email sent successfully');
      setReplyData({ id: '', subject: '', description: '' });
    } catch (error) {
      toast.error('Failed to send email');
      console.error('Reply error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await apiService.get(`contactUsStatus/${id}`);
      setContacts(contacts.map(c => c._id === id ? { ...c, status: newStatus } : c));
    } catch (error) {
      console.error('Status update error:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const totalRecords = contacts.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentContacts = contacts.slice(startIndex, endIndex);

  return (
    <div className="p-0">
      <nav className="flex flex-wrap items-center text-xs sm:text-sm mb-3">
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900 font-medium">Contact Us</span>
      </nav>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6 border-b">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Search by name, email or subject"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full sm:w-full sm:w-[150px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="All">Show All</option>
              <option value="1">Open</option>
              <option value="0">Close</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full sm:w-full sm:w-[150px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="All">Sort By Latest</option>
              <option value="1">Sort By Open</option>
              <option value="0">Sort By Close</option>
            </select>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{borderColor: '#009ef7'}}></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-12">
              <i className="bi bi-envelope-open text-4xl text-gray-300 mb-2"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Contact Requests</h3>
              <p className="text-gray-500">No contact us submissions found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500 text-xs uppercase">
                    <th className="px-2 sm:px-4 py-3">Index</th>
                    <th className="px-2 sm:px-4 py-3">User Details</th>
                    <th className="px-2 sm:px-4 py-3 min-w-[300px]">Subject & Message</th>
                    <th className="px-2 sm:px-4 py-3">Date</th>
                    <th className="px-2 sm:px-4 py-3">Status</th>
                    <th className="px-2 sm:px-4 py-3">Reply Status</th>
                    <th className="px-2 sm:px-4 py-3 text-center">Action</th>
                    <th className="px-2 sm:px-4 py-3 text-center">Reply</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {currentContacts.map((contact, index) => (
                    <tr key={contact._id} className="hover:bg-gray-50">
                      <td className="px-2 sm:px-2 sm:px-4 py-3 text-sm">{startIndex + index + 1}</td>
                      <td className="px-2 sm:px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <div className="text-sm font-medium text-gray-900">{contact.fullName}</div>
                          <div className="text-sm text-gray-600">{contact.email}</div>
                          {contact.phone && <div className="text-sm text-gray-600">{contact.phone}</div>}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3">
                        <div className="flex flex-col gap-2">
                          <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold w-fit" style={{backgroundColor: '#e8f4fd', color: '#009ef7'}}>
                            {contact.subject}
                          </span>
                          <div className="text-sm text-gray-700">{contact.message}</div>
                        </div>
                      </td>
                      <td className="px-2 sm:px-2 sm:px-4 py-3 text-sm">{formatDate(contact.createdAt)}</td>
                      <td className="px-2 sm:px-4 py-3">
                        <select
                          value={contact.status === 1 ? 'open' : 'close'}
                          onChange={(e) => handleStatusChange(contact._id, e.target.value === 'open' ? 1 : 0)}
                          className="text-sm border border-gray-300 rounded px-2 py-1"
                        >
                          <option value="open">Open</option>
                          <option value="close">Close</option>
                        </select>
                      </td>
                      <td className="px-2 sm:px-4 py-3">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs ${contact.reply?.body ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {contact.reply?.body ? 'Replied' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-center">
                        <button
                          onClick={() => handleDelete(contact._id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <i className="bi bi-trash text-lg"></i>
                        </button>
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-center">
                        <button
                          onClick={() => handleReply(contact._id)}
                          className="px-3 py-1 bg-green-100 text-green-700 rounded text-xs font-medium hover:bg-green-200"
                        >
                          Send
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {contacts.length > 0 && (
          <div className="bg-white px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-gray-200">
            <div>
              <p className="text-xs sm:text-sm text-gray-700">
                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                <span className="font-medium">{Math.min(endIndex, totalRecords)}</span> of{' '}
                <span className="font-medium">{totalRecords}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-xs sm:text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  if (endPage - startPage < 4) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  const pageNum = startPage + i;
                  if (pageNum > totalPages) return null;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`relative inline-flex items-center px-2 sm:px-4 py-2 border text-xs sm:text-sm font-medium ${
                        currentPage === pageNum
                          ? 'z-10 border-[#009ef7] text-white'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                      style={currentPage === pageNum ? {backgroundColor: '#009ef7'} : {}}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-xs sm:text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Confirmation</h3>
              <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this ContactUs Info?</p>
            <div className="flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-2 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Send Email</h3>
              <button onClick={() => setShowReplyModal(false)} className="text-gray-400 hover:text-gray-600">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <form onSubmit={submitReply}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={replyData.subject}
                  onChange={(e) => setReplyData({ ...replyData, subject: e.target.value })}
                  placeholder="Enter Subject"
                  className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={replyData.description}
                  onChange={(e) => setReplyData({ ...replyData, description: e.target.value })}
                  placeholder="Enter Description"
                  rows="8"
                  className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  required
                />
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowReplyModal(false)}
                  className="px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-2 sm:px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                  style={{backgroundColor: '#009ef7'}}
                  disabled={submitting}
                >
                  {submitting ? 'Sending...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactUs;
